import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import i18n from "../i18n";
import {
  Sparkles, Crown, BarChart3, LineChart, Map, CalendarDays, NotebookPen, Users,
  Globe, FileDown, ShieldCheck, Search, Tags, Layers, Mic2, Newspaper,
  RotateCcw, Zap, Image as ImageIcon, Check, X as XIcon,
} from "lucide-react";

// Mini hook i18n safe (évite TS2589)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const Features: React.FC = () => {
  const { t } = useI18nSafe();

  const sections = [
    {
      id: "stats",
      title: t("features:sections.stats.title"),
      desc: t("features:sections.stats.desc"),
      items: [
        { icon: BarChart3, title: t("features:sections.stats.items.0.title"), desc: t("features:sections.stats.items.0.desc") },
        { icon: LineChart, title: t("features:sections.stats.items.1.title"), desc: t("features:sections.stats.items.1.desc") },
        { icon: FileDown,  title: t("features:sections.stats.items.2.title"), desc: t("features:sections.stats.items.2.desc") },
      ],
    },
    {
      id: "matchday",
      title: t("features:sections.matchday.title"),
      desc: t("features:sections.matchday.desc"),
      items: [
        { icon: Zap,         title: t("features:sections.matchday.items.0.title"), desc: t("features:sections.matchday.items.0.desc") },
        { icon: RotateCcw,   title: t("features:sections.matchday.items.1.title"), desc: t("features:sections.matchday.items.1.desc") },
        { icon: CalendarDays,title: t("features:sections.matchday.items.2.title"), desc: t("features:sections.matchday.items.2.desc") },
      ],
    },
    {
      id: "play",
      title: t("features:sections.play.title"),
      desc: t("features:sections.play.desc"),
      items: [
        { icon: NotebookPen, title: t("features:sections.play.items.0.title"), desc: t("features:sections.play.items.0.desc") },
        { icon: Tags,        title: t("features:sections.play.items.1.title"), desc: t("features:sections.play.items.1.desc") },
        { icon: Globe,       title: t("features:sections.play.items.2.title"), desc: t("features:sections.play.items.2.desc") },
        { icon: Layers,      title: t("features:sections.play.items.3.title"), desc: t("features:sections.play.items.3.desc") },
      ],
    },
    {
      id: "club",
      title: t("features:sections.club.title"),
      desc: t("features:sections.club.desc"),
      items: [
        { icon: Users,        title: t("features:sections.club.items.0.title"), desc: t("features:sections.club.items.0.desc") },
        { icon: CalendarDays, title: t("features:sections.club.items.1.title"), desc: t("features:sections.club.items.1.desc") },
        { icon: Mic2,         title: t("features:sections.club.items.2.title"), desc: t("features:sections.club.items.2.desc") },
        { icon: Newspaper,    title: t("features:sections.club.items.3.title"), desc: t("features:sections.club.items.3.desc") },
      ],
    },
    {
      id: "premium",
      title: t("features:sections.premium.title"),
      desc: t("features:sections.premium.desc"),
      items: [
        { icon: Crown,      title: t("features:sections.premium.items.0.title"), desc: t("features:sections.premium.items.0.desc") },
        { icon: ImageIcon,  title: t("features:sections.premium.items.1.title"), desc: t("features:sections.premium.items.1.desc") },
        { icon: Layers,     title: t("features:sections.premium.items.2.title"), desc: t("features:sections.premium.items.2.desc") },
        { icon: NotebookPen,title: t("features:sections.premium.items.3.title"), desc: t("features:sections.premium.items.3.desc") },
        { icon: Map,        title: t("features:sections.premium.items.4.title"), desc: t("features:sections.premium.items.4.desc") },
      ],
    },
  ];

  const comparison = [
    { label: t("features:comparison.rows.0"), free: true,  pro: true  },
    { label: t("features:comparison.rows.1"), free: true,  pro: true  },
    { label: t("features:comparison.rows.2"), free: true,  pro: true  },
    { label: t("features:comparison.rows.3"), free: true,  pro: true  },
    { label: t("features:comparison.rows.4"), free: false, pro: true  },
    { label: t("features:comparison.rows.5"), free: false, pro: true  },
    { label: t("features:comparison.rows.6"), free: false, pro: true  },
    { label: t("features:comparison.rows.7"), free: false, pro: true  },
    { label: t("features:comparison.rows.8"), free: false, pro: true  },
  ];

  return (
    <section className="relative min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Fond déco */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-[-12rem] h-[28rem] w-[60rem] -translate-x-1/2 bg-gradient-to-r from-orange-500/20 via-fuchsia-500/20 to-cyan-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.16),_transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.06),_transparent_60%)]" />
      </div>

      <div className="mx-auto max-w-6xl px-4 py-16">
        {/* Hero */}
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-8 shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-300" />
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("features:hero.badge")}
            </span>
          </div>
          <h1 className="mt-3 text-center font-[Rubik] text-3xl font-bold italic text-orange-600 dark:text-orange-400 sm:text-4xl">
            {t("features:hero.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-center text-gray-700 dark:text-gray-300">
            {t("features:hero.subtitle")}
          </p>

          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform hover:-translate-y-0.5 hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 dark:ring-0"
            >
              <Sparkles className="h-5 w-5" />
              {t("features:hero.ctaSignup")}
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-5 py-2.5 font-semibold text-gray-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
            >
              {t("features:hero.ctaLogin")}
            </Link>
          </div>

          {/* mini nav d’ancre */}
          <nav className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-gray-200 bg-white/70 px-3 py-1 transition-colors hover:bg-white dark:border-white/10 dark:bg-white/5"
              >
                {s.title}
              </a>
            ))}
          </nav>
        </div>

        {/* Sections */}
        <div className="mt-10 space-y-12">
          {sections.map((section) => (
            <div id={section.id} key={section.id} className="scroll-mt-24">
              <header className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{section.desc}</p>
                </div>
                <Search className="h-5 w-5 opacity-40" />
              </header>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {section.items.map((it, i) => (
                  <div
                    key={i}
                    className="group rounded-xl border border-gray-200 bg-white/80 p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="mb-3 inline-flex rounded-lg border border-orange-200/60 bg-orange-50/70 p-2 dark:border-orange-300/20 dark:bg-orange-300/10">
                      <it.icon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                    </div>
                    <h3 className="mb-1 font-semibold">{it.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{it.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Comparatif Free vs Premium */}
        <div className="mx-auto mt-12 max-w-5xl rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t("features:comparison.title")}</h3>
            <Crown className="h-5 w-5 text-orange-600 dark:text-orange-300" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 dark:border-white/10 dark:text-gray-400">
                  <th className="p-2">{t("features:comparison.col.feature")}</th>
                  <th className="p-2">{t("features:comparison.col.free")}</th>
                  <th className="p-2">{t("features:comparison.col.pro")}</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-100 last:border-0 dark:border-white/5">
                    <td className="p-2">{row.label}</td>
                    <td className="p-2">
                      {row.free ? <Check className="h-4 w-4 text-green-600" /> : <XIcon className="h-4 w-4 text-gray-400" />}
                    </td>
                    <td className="p-2">
                      {row.pro ? <Check className="h-4 w-4 text-green-600" /> : <XIcon className="h-4 w-4 text-gray-400" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA Premium */}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("features:comparison.ctaLead")}
            </p>
            <div className="flex gap-2">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform hover:-translate-y-0.5 hover:bg-orange-700"
              >
                {t("features:comparison.ctaSignup")}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-4 py-2 font-semibold text-gray-900 shadow-sm hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
              >
                {t("features:comparison.ctaPricing")}
              </Link>
            </div>
          </div>
        </div>

        {/* Extras */}
        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-1 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h4 className="font-semibold">{t("features:extras.security.title")}</h4>
            </header>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("features:extras.security.desc")}
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
            <header className="mb-1 flex items-center gap-2">
              <Newspaper className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              <h4 className="font-semibold">{t("features:extras.comms.title")}</h4>
            </header>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("features:extras.comms.desc")}
            </p>
          </div>
        </div>

        {/* Footer callout */}
        <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-6 text-center shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("features:footer.callout")}
          </p>
          <div className="mt-3">
            <a
              href="https://discord.gg/q5zR75uTB6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white/70 px-5 py-2.5 font-semibold text-gray-900 shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
            >
              {t("features:footer.discord")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
